import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { PANEL_STATUS, type PanelStatus } from './panelStatuses';
import { useFormContext } from 'react-hook-form';

import {
  usePanelState,
  usePanelDispatch,
} from '../../../../financeProviders/shared/PanelContext';
import { CollapsiblePanelNames } from '../../../../queries/schema/carFinance.schema';

type CollapsiblePanelProviderProps = {
  children: ReactNode;
  panelName: CollapsiblePanelNames;
};

type CollapsiblePanelContext = {
  status: PanelStatus;
  addField: (path: Field) => void;
  removeField: (path: Field) => void;
};

const CollapsiblePanelContext = createContext<CollapsiblePanelContext | null>(
  null,
);

type Field = {
  path: string;
  invalid: boolean;
  value: number | string | boolean | undefined;
  isOptional: boolean;
};

/**
 * This is a React context that is intended to wrap the `CollapsiblePanel` component. It is to be used in conjunction with form fields that publish their state to the context. This is done via the `usePanelContentsSubscribe` hook. An example of this can be seen with the `FinanceTextField` component.
 *
 * The context requires knowledge of the fields within it as it is required to determine a status indicating the validity of fields contained within.
 *
 * This advantages of this approach where each field publishes it's state to the panel context (which then dictates the panel UI) are that:
 * 1. The Panel and TextField do not need to define business requirements internally, as the provider and subscriber components do this for them.
 * 2. This behaviour is maintained automatically as new fields are added and removed. Another approach was considered using a simple hook but there was no coupling between the fields the hook subscribed to and the actual fields in the panel.
 *
 *
 * @example
 * ```tsx
 * <CollapsiblePanelProvider defaultIsOpen>
 *  <CollapsiblePanel>
 *   <Controller
 *    control={control}
 *    render={({ field, fieldState }) => (
 *     <FinanceTextField {...field} {...fieldState} />
 *    )}
 *   />
 *  </CollapsiblePanel>
 * </CollapsiblePanelProvider>
 * ```
 *
 */
const CollapsiblePanelProvider = (props: CollapsiblePanelProviderProps) => {
  const { children, panelName } = props;
  const [fields, setFields] = useState<Field[]>([]);
  const {
    formState: { isDirty },
  } = useFormContext();

  const panelState = usePanelState();
  const dispatch = usePanelDispatch();

  const addField = useCallback(
    (field: Field) => setFields((prev) => [...prev, field]),
    [],
  );

  const removeField = useCallback(
    (field: Field) =>
      setFields((prev) => prev.filter((p) => p.path !== field.path)),
    [],
  );

  const status = panelState[panelName].isValid
    ? PANEL_STATUS.COMPLETE
    : PANEL_STATUS.INCOMPLETE;

  useEffect(() => {
    const requiredFields = fields.filter((field) => !field.isOptional);
    const allRequiredFieldsHaveValues = requiredFields.every(
      // We allow zero as a value for certain fields
      ({ value }) => value || typeof value === 'number',
    );
    const atLeastOneIsInvalid = requiredFields.some((field) => field.invalid);
    if (isDirty && allRequiredFieldsHaveValues && !atLeastOneIsInvalid) {
      dispatch({
        type: 'VALIDATE_PANEL',
        panel: panelName,
        isValid: true,
      });
    } else {
      dispatch({
        type: 'INVALIDATE_PANEL',
        panel: panelName,
      });
    }
  }, [fields, panelName, dispatch, isDirty]);

  const value = useMemo(
    () => ({
      status,
      addField,
      removeField,
    }),
    [status, addField, removeField],
  );

  return (
    <CollapsiblePanelContext.Provider value={value}>
      {children}
    </CollapsiblePanelContext.Provider>
  );
};

const useCollapsiblePanelContext = () => {
  const context = useContext(CollapsiblePanelContext);
  if (!context) {
    throw new Error(
      'useCollapsiblePanelContext must be used within a CollapsiblePanelProvider',
    );
  }
  return context;
};

export { CollapsiblePanelProvider, useCollapsiblePanelContext };
